import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './Table.less';

const detectDelimiter = (line) => {
  if (typeof line !== 'string') return ';';
  const semicolon = (line.match(/;/g) || []).length;
  const comma = (line.match(/,/g) || []).length;
  const tab = (line.match(/\t/g) || []).length;
  if (semicolon === 0 && comma === 0 && tab === 0) return ';';
  if (semicolon >= comma && semicolon >= tab) return ';';
  if (comma >= tab) return ',';
  return '\t';
};

const splitLine = (line, delimiter = ';') => {
  if (typeof line !== 'string') return [];
  return line.split(delimiter).map((cell) => cell.trim());
};

const splitCells = (value, delimiter) => {
  if (Array.isArray(value)) {
    if (value.length === 1 && typeof value[0] === 'string' && /[;,\t]/.test(value[0])) {
      return splitLine(value[0], delimiter || detectDelimiter(value[0]));
    }
    return value;
  }
  if (typeof value === 'string') return splitLine(value, delimiter || detectDelimiter(value));
  return [];
};

const padCells = (cells, columnCount) => {
  const next = (cells || []).slice(0, columnCount);
  while (next.length < columnCount) next.push('');
  return next;
};

const Table = ({ LockFirstColumn, FirstColumnIsProductLinked, Headers, Rows }) => {
  const scrollRef = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setOverflowing(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScroll();
    const resizeObserver = new ResizeObserver(updateScroll);
    resizeObserver.observe(el);
    window.addEventListener('resize', updateScroll);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScroll);
    };
  }, [Headers, Rows, updateScroll]);

  const headerSource = Array.isArray(Headers) ? Headers.join(';') : Headers;
  const delimiter = detectDelimiter(headerSource);
  const headers = splitCells(Headers, delimiter);
  if (!headers.length) return null;

  const rows = (Rows || []).map((row) => ({
    ...row,
    Cells: padCells(splitCells(row.Cells, delimiter), headers.length),
  }));
  const lockFirstColumn =
    LockFirstColumn === true || LockFirstColumn === 1 || LockFirstColumn === '1';
  const firstColumnIsProductLinked =
    FirstColumnIsProductLinked === true ||
    FirstColumnIsProductLinked === 1 ||
    FirstColumnIsProductLinked === '1';
  const classNames = ['table-component'];
  if (lockFirstColumn) classNames.push('table-component--lock-first');
  if (overflowing) classNames.push('table-component--scroll');

  return (
    <div className={classNames.join(' ')}>
      <div className="table-component__scroll scrollBar--3" ref={scrollRef}>
        <table className="table-component__table">
          <thead>
            <tr>
              {headers.map((header, index) => (
                <th key={`h-${index}`} scope="col" className="table-component__cell table-component__cell--title heading6">
                  <span>{header}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={`r-${rowIndex}`}>
                {(row.Cells || []).map((cell, cellIndex) => {
                  const isFirstColumn = cellIndex === 0;
                  const isProduct = isFirstColumn && firstColumnIsProductLinked && row.Product;
                  const isHeadingColumn =
                    isFirstColumn && (lockFirstColumn || firstColumnIsProductLinked);
                  const typeClass = isHeadingColumn ? 'heading6' : 'paragraph--s';
                  const cellClass = [
                    'table-component__cell',
                    isProduct ? 'table-component__cell--product' : '',
                    typeClass,
                  ]
                    .filter(Boolean)
                    .join(' ');
                  return (
                    <td key={`c-${rowIndex}-${cellIndex}`} className={cellClass}>
                      {isProduct && row.Product.Url ? (
                        <a href={row.Product.Url} className="table-component__product-link">
                          {row.Product.Name}
                        </a>
                      ) : isProduct ? (
                        <span className="table-component__product-link">{row.Product.Name}</span>
                      ) : (
                        <span>{cell}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const byId = typeof window !== 'undefined' && window._THULEDATA && window._THULEDATA._tableById;

if (byId && typeof byId === 'object') {
  Object.keys(byId).forEach((instanceId) => {
    const rootEl = document.getElementById(`REACT_table_${instanceId}`);
    const payload = byId[instanceId];
    if (!rootEl || !payload) return;
    const root = createRoot(rootEl);
    root.render(
      <Sentry.ErrorBoundary>
        <Table {...payload} />
      </Sentry.ErrorBoundary>
    );
  });
}

export default Table;
